import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { companyInfo, services } from '../data/mock';
import SEO from '../components/ui/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const ContactPage = () => {
  const pageRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.fromTo('.contact-hero',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
      );

      gsap.fromTo('.contact-form',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.7, delay: 0.2, ease: 'power3.out' }
      );

      gsap.fromTo('.contact-info',
        { opacity: 0, x: 30 },
        { opacity: 1, x: 0, duration: 0.6, stagger: 0.1, delay: 0.3, ease: 'power3.out' }
      );
    });

    return () => ctx.revert();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      toast.success('Thank you for your enquiry!', {
        description: 'We will respond within 24 hours.',
      });
      setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <>
      <SEO
        title="Contact Us — Let's Start a Conversation"
        description="Get in touch with VISO Connect. We respond to all enquiries within 24 hours. Reach us by email, phone, or submit our contact form."
        canonical="https://visoconnect.in/contact"
      />
      <div ref={pageRef} className="overflow-hidden bg-[#0a0a0f]">
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#0a0a0f] relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-indigo-500/10 rounded-full blur-[150px] pointer-events-none" />

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
            <div className="contact-hero text-center">
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                Contact Us
              </span>
              <h1 className="text-white mb-6" style={{
                fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.03em'
              }}>
                Let's Start a{' '}
                <span className="gradient-text">Conversation</span>
              </h1>
              <p className="text-slate-400 mx-auto" style={{ maxWidth: '480px', fontSize: '1.05rem' }}>
                Have a question or want to discuss a solution? We're here to help.
              </p>
            </div>
          </div>
        </section>

        {/* Form & Info */}
        <section className="py-20 bg-[#111118]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-10">
              {/* Form */}
              <div className="lg:col-span-2">
                <div className="contact-form card-dark p-8 lg:p-10">
                  <h2 className="text-xl font-bold text-white mb-2">Send a Message</h2>
                  <p className="text-slate-500 text-sm mb-8">We respond within 24 hours.</p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300 font-medium text-sm">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          required
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300 font-medium text-sm">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@company.co.uk"
                          required
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-slate-300 font-medium text-sm">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Your Company Ltd"
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300 font-medium text-sm">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+44 20 7946 0958"
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-slate-300 font-medium text-sm">Service Interest</Label>
                      <Select value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger className="h-11 bg-white/5 border-white/10 text-white rounded-xl">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a24] border-white/10">
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id} className="text-white hover:bg-white/10">
                              {service.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="other" className="text-white hover:bg-white/10">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300 font-medium text-sm">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your requirements..."
                        required
                        rows={4}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500 resize-none rounded-xl"
                      />
                    </div>

                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-glow bg-gradient-to-r from-indigo-500 to-cyan-500 text-white px-8 py-5 text-sm font-semibold rounded-xl border-0 w-full sm:w-auto"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Send Message
                            <Send size={16} />
                          </span>
                        )}
                      </Button>
                    </div>

                    <p className="text-slate-600 text-xs">
                      By submitting, you agree to our Privacy Policy.
                    </p>
                  </form>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-5">
                <div className="contact-info card-dark p-6">
                  <h3 className="font-bold text-white mb-5 text-sm">Contact Information</h3>
                  <div className="space-y-5">
                    <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-3 group">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/30 transition-colors">
                        <Mail className="text-indigo-400" size={18} />
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs mb-0.5">Email</p>
                        <p className="text-white text-sm font-medium group-hover:text-indigo-400 transition-colors">
                          {companyInfo.email}
                        </p>
                      </div>
                    </a>

                    <a href={`tel:${companyInfo.phone}`} className="flex items-start gap-3 group">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/30 transition-colors">
                        <Phone className="text-indigo-400" size={18} />
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs mb-0.5">Phone</p>
                        <p className="text-white text-sm font-medium group-hover:text-indigo-400 transition-colors">
                          {companyInfo.phone}
                        </p>
                      </div>
                    </a>

                    {companyInfo.phone2 && (
                      <a href={`tel:${companyInfo.phone2}`} className="flex items-start gap-3 group">
                        <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/30 transition-colors">
                          <Phone className="text-indigo-400" size={18} />
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs mb-0.5">Phone (Alt)</p>
                          <p className="text-white text-sm font-medium group-hover:text-indigo-400 transition-colors">
                            {companyInfo.phone2}
                          </p>
                        </div>
                      </a>
                    )}

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-indigo-400" size={18} />
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs mb-0.5">Address</p>
                        <p className="text-white text-sm font-medium">
                          {companyInfo.address.street}<br />
                          {companyInfo.address.city}, {companyInfo.address.postcode}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="contact-info card-dark p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="text-indigo-400" size={18} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-2 text-sm">Hours</h3>
                      <div className="space-y-1 text-slate-400 text-sm">
                        <p>Mon–Fri: 9:00–18:00</p>
                        <p>Sat: 10:00–14:00</p>
                      </div>
                      <p className="text-emerald-400 font-medium mt-3 text-xs">
                        24/7 support for clients
                      </p>
                    </div>
                  </div>
                </div>

                <div className="contact-info bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-2xl p-6 border border-indigo-500/20">
                  <h3 className="font-bold text-white mb-2 text-sm">Response Guarantee</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    All enquiries answered within 24 hours during business days.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
